import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Paper, IconButton, Typography, Tooltip } from '@mui/material';
import ScreenLockPortraitIcon from '@mui/icons-material/ScreenLockPortrait';
import PhonelinkLockIcon from '@mui/icons-material/PhonelinkLock';

const ScreenLockWidget = () => {
  const [isActive, setIsActive] = useState(false);
  const [isSupported, setIsSupported] = useState(true);
  const sentinelRef = useRef(null);

  useEffect(() => {
    if (!('wakeLock' in navigator)) {
      setIsSupported(false);
    }
  }, []);

  const releaseLock = useCallback(async () => {
    if (sentinelRef.current) {
      try {
        await sentinelRef.current.release();
      } catch (err) {
        console.warn('Failed to release wake lock:', err);
      }
      sentinelRef.current = null;
    }
    setIsActive(false);
  }, []);

  const requestLock = useCallback(async () => {
    if (!isSupported) return;
    try {
      // If we already have a lock, don't request another one
      if (sentinelRef.current) return;

      const sentinel = await navigator.wakeLock.request('screen');
      sentinelRef.current = sentinel;
      setIsActive(true);

      // If the lock is released by the system (e.g. low battery), update state
      sentinel.addEventListener('release', () => {
        // Only update state if this specific sentinel was the active one
        if (sentinelRef.current === sentinel) {
           setIsActive(false);
           sentinelRef.current = null;
        }
      });
    } catch (err) {
      console.error('Failed to acquire wake lock:', err);
      setIsActive(false);
    }
  }, [isSupported]);

  const toggleLock = useCallback(() => {
    if (isActive) {
      releaseLock();
    } else {
      requestLock();
    }
  }, [isActive, requestLock, releaseLock]);

  // 1. Auto-request lock on mount
  useEffect(() => {
    requestLock();
  }, [requestLock]);

  // 2. Re-acquire lock if page becomes visible again and it was previously active
  //    or if we just mounted and the tab is visible.
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && !sentinelRef.current) {
        await requestLock();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [requestLock]);

  // 3. Cleanup on unmount (release lock)
  useEffect(() => {
    return () => {
      releaseLock();
    };
  }, [releaseLock]);

  if (!isSupported) return null;

  return (
    <Paper
      variant="outlined"
      sx={{
        py: 0.25,
        px: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        minWidth: 'auto',
        borderRadius: 2,
        bgcolor: isActive ? 'rgba(76, 175, 80, 0.08)' : 'background.paper',
        border: 1,
        borderColor: isActive ? 'success.main' : 'divider',
        boxShadow: 1,
        transition: 'all 0.3s ease'
      }}
    >
      <Tooltip title={isActive ? "Screen Lock Active (Tap to disable)" : "Screen Lock Disabled (Tap to enable)"}>
        <IconButton 
          size="small" 
          onClick={toggleLock} 
          color={isActive ? "success" : "default"}
          sx={{ padding: 0.5 }}
        >
          {isActive ? <ScreenLockPortraitIcon fontSize="small" /> : <PhonelinkLockIcon fontSize="small" />}
        </IconButton>
      </Tooltip>
      
      {isActive && (
        <Typography 
          variant="caption" 
          sx={{ 
            fontWeight: 'bold', 
            color: 'success.main',
            userSelect: 'none',
            mr: 0.5
          }}
        >
          ON
        </Typography>
      )}
    </Paper>
  );
};

export default ScreenLockWidget;